import React, { useState, useEffect, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { apiService, apiHelpers } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './Reader.css';

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Unknown';
  }
};

const groupFeeds = (feeds) => {
  const groups = { healthy: [], failing: [], idle: [] };
  feeds.forEach((feed) => {
    if (feed.status === 'success') groups.healthy.push(feed);
    else if (feed.status === 'error') groups.failing.push(feed);
    else groups.idle.push(feed);
  });
  return groups;
};

const latestFeedUpdate = (feeds) =>
  feeds.reduce((latest, feed) => {
    if (!feed.last_updated) return latest;
    const updated = new Date(feed.last_updated);
    if (Number.isNaN(updated.getTime())) return latest;
    if (!latest || updated > latest) return updated;
    return latest;
  }, null);

const statusWithFeedFreshness = (status, feeds) => {
  if (!status) return null;
  const latest = latestFeedUpdate(feeds);
  if (!latest) return status;

  const statusLastPoll = status.last_poll ? new Date(status.last_poll) : null;
  if (statusLastPoll && !Number.isNaN(statusLastPoll.getTime()) && statusLastPoll >= latest) {
    return status;
  }

  const nextPoll = new Date(latest.getTime() + (status.poll_interval_hours || 24) * 3600000);
  return {
    ...status,
    last_poll: latest.toISOString(),
    next_poll: nextPoll.toISOString(),
  };
};

const dormantDays = (dormantSince) => {
  if (!dormantSince) return null;
  const ms = Date.now() - new Date(dormantSince).getTime();
  const days = Math.floor(ms / 86400000);
  return days >= 0 ? days : null;
};

const StatusPage = () => {
  const [systemStatus, setSystemStatus] = useState(null);
  const [feedStatus, setFeedStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [enabling, setEnabling] = useState({});

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusResponse, feedsResponse] = await Promise.all([
        apiService.getStatus(),
        apiService.getFeeds(),
      ]);

      setSystemStatus(statusResponse.data);
      setFeedStatus(feedsResponse.data || []);
    } catch (err) {
      console.error('Error loading status:', err);
      setError(apiHelpers.formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const triggerPoll = async () => {
    try {
      setPolling(true);
      await apiService.triggerPoll();
      setTimeout(loadStatus, 2000);
    } catch (err) {
      console.error('Error triggering poll:', err);
      setError(apiHelpers.formatError(err));
    } finally {
      setPolling(false);
    }
  };

  const handleEnable = async (feed) => {
    setEnabling((prev) => ({ ...prev, [feed.source_name]: true }));
    try {
      await apiService.enableFeed(feed.source_name);
      await loadStatus();
    } catch (err) {
      setError(apiHelpers.formatError(err));
    } finally {
      setEnabling((prev) => ({ ...prev, [feed.source_name]: false }));
    }
  };

  const displayStatus = useMemo(
    () => statusWithFeedFreshness(systemStatus, feedStatus),
    [systemStatus, feedStatus],
  );
  const groups = useMemo(() => groupFeeds(feedStatus), [feedStatus]);
  const totalSources = feedStatus.length;
  const lastUpdate = displayStatus?.last_poll
    ? formatDate(displayStatus.last_poll)
    : 'never';

  if (loading && !systemStatus) {
    return (
      <div className="pb-page">
        <LoadingSpinner text="Loading system status..." />
      </div>
    );
  }

  return (
    <div className="pb-page">
      <div className="pb-ticker" role="status" aria-live="polite">
        <span className="pb-ticker-live">
          <span className="pb-ticker-dot" aria-hidden="true" />
          LIVE
        </span>
        <span>
          Scouring {totalSources.toLocaleString()}{' '}
          {totalSources === 1 ? 'source' : 'sources'}
        </span>
        <span className="pb-ticker-sep" aria-hidden="true">·</span>
        <span>
          {displayStatus?.total_articles?.toLocaleString() || 0} stories archived
        </span>
        <span className="pb-ticker-sep" aria-hidden="true">·</span>
        <span>Last update {lastUpdate}</span>
        <span className="pb-ticker-sep" aria-hidden="true">·</span>
        <span style={{ color: 'var(--pb-accent)' }}>
          {groups.healthy.length} HEALTHY · {groups.failing.length} FAILING
        </span>
      </div>

      <header className="pb-page-header">
        <div>
          <span className="pb-page-eyebrow">▸ System · Status</span>
          <h1 className="pb-page-title">System Status</h1>
        </div>
        <div className="pb-page-actions">
          <button
            type="button"
            className="pb-chip"
            onClick={loadStatus}
            disabled={loading}
          >
            ↻ Refresh
          </button>
          <button
            type="button"
            className="pb-chip is-primary"
            onClick={triggerPoll}
            disabled={polling}
          >
            {polling ? '◌ Polling...' : '▸ Trigger Poll'}
          </button>
        </div>
      </header>

      {error && (
        <div className="pb-error" role="alert">
          <span>
            <strong>Error:</strong> {error}
          </span>
        </div>
      )}

      {displayStatus && (
        <section className="pb-scour-card" aria-label="Scour stats">
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Total Articles</span>
            <span className="pb-scour-stat-value">
              {displayStatus.total_articles.toLocaleString()}
            </span>
            <span className="pb-scour-stat-meta">archived</span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">RSS Feeds</span>
            <span className="pb-scour-stat-value">
              {displayStatus.total_feeds}
            </span>
            <span className="pb-scour-stat-meta">tracked</span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Poll Interval</span>
            <span className="pb-scour-stat-value">
              {displayStatus.poll_interval_hours}h
            </span>
            <span className="pb-scour-stat-meta">cadence</span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">System Status</span>
            <span className="pb-scour-stat-value">{displayStatus.status}</span>
            <span className="pb-scour-stat-meta">overall</span>
          </div>
        </section>
      )}

      {displayStatus && (
        <section aria-label="Polling Schedule">
          <div className="pb-section-head">
            <h2 className="pb-section-title">Polling Schedule</h2>
            <span className="pb-section-meta">
              Every {displayStatus.poll_interval_hours} hours
            </span>
          </div>
          <div className="pb-feed-row">
            <span className="pb-feed-dot pb-feed-dot--idle" aria-hidden="true" />
            <div className="pb-feed-name">
              <span className="pb-feed-name-label">Last Poll:</span>
              <span className="pb-feed-name-error" style={{ color: 'var(--pb-ink-muted)' }}>
                {formatDate(displayStatus.last_poll)}
              </span>
            </div>
            <span className="pb-feed-meta">
              Next Poll: {formatDate(displayStatus.next_poll)}
            </span>
          </div>
        </section>
      )}

      <section aria-label="RSS Feed Status">
        <div className="pb-section-head">
          <h2 className="pb-section-title">RSS Feed Status</h2>
          <span className="pb-section-meta">
            {totalSources} {totalSources === 1 ? 'source' : 'sources'}
            {loading ? ' · (updating...)' : ''}
          </span>
        </div>

        {feedStatus.length === 0 ? (
          <div className="pb-empty">
            No feed status information available
          </div>
        ) : (
          <>
            {groups.healthy.length > 0 && (
              <FeedGroup
                title="Healthy"
                count={groups.healthy.length}
                feeds={groups.healthy}
                dotClass=""
                onEnable={handleEnable}
                enabling={enabling}
              />
            )}
            {groups.failing.length > 0 && (
              <FeedGroup
                title="Failing"
                count={groups.failing.length}
                feeds={groups.failing}
                dotClass="pb-feed-dot--fail"
                onEnable={handleEnable}
                enabling={enabling}
              />
            )}
            {groups.idle.length > 0 && (
              <FeedGroup
                title="Idle"
                count={groups.idle.length}
                feeds={groups.idle}
                dotClass="pb-feed-dot--idle"
                onEnable={handleEnable}
                enabling={enabling}
              />
            )}
          </>
        )}
      </section>

      {displayStatus && (
        <section className="pb-scour-card" aria-label="Feed health summary">
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Successful Feeds</span>
            <span className="pb-scour-stat-value">
              {displayStatus.successful_feeds || 0}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Feeds with Errors</span>
            <span className="pb-scour-stat-value">
              {displayStatus.feeds_with_errors || 0}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Feeds with 0 Articles</span>
            <span className="pb-scour-stat-value">
              {displayStatus.feeds_with_zero_articles || 0}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Disabled Feeds</span>
            <span className="pb-scour-stat-value">
              {displayStatus.disabled_feeds || 0}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">New Articles (24h)</span>
            <span className="pb-scour-stat-value">
              {displayStatus.recent_articles_24h || 0}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Active Sources</span>
            <span className="pb-scour-stat-value">
              {Object.keys(displayStatus.articles_by_source_count || {}).length}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Latest Article</span>
            <span className="pb-scour-stat-value" style={{ fontSize: '15px' }}>
              {formatDate(displayStatus.newest_article_date)}
            </span>
          </div>
          <div className="pb-scour-stat">
            <span className="pb-scour-stat-label">Oldest Article</span>
            <span className="pb-scour-stat-value" style={{ fontSize: '15px' }}>
              {formatDate(displayStatus.oldest_article_date)}
            </span>
          </div>
        </section>
      )}

      {displayStatus &&
        displayStatus.articles_by_source_count &&
        Object.keys(displayStatus.articles_by_source_count).length > 0 && (
          <section aria-label="Articles by Source">
            <div className="pb-section-head">
              <h2 className="pb-section-title">Articles by Source</h2>
              <span className="pb-section-meta">
                {Object.keys(displayStatus.articles_by_source_count).length}{' '}
                sources
              </span>
            </div>
            <div className="pb-list">
              {Object.entries(displayStatus.articles_by_source_count)
                .sort(([, a], [, b]) => b - a)
                .map(([source, count]) => (
                  <div key={source} className="pb-feed-row">
                    <span
                      className={`pb-feed-dot ${count === 0 ? 'pb-feed-dot--fail' : ''}`}
                      aria-hidden="true"
                    />
                    <div className="pb-feed-name">
                      <span className="pb-feed-name-label">{source}</span>
                    </div>
                    <span className="pb-feed-meta">
                      <span className="pb-feed-meta-count">{count}</span>
                      articles
                    </span>
                  </div>
                ))}
            </div>
          </section>
        )}
    </div>
  );
};

const FeedGroup = ({ title, count, feeds, dotClass, onEnable, enabling }) => (
  <div>
    <div className="pb-section-head" style={{ borderTop: 'none', paddingTop: 8 }}>
      <h3 className="pb-section-title" style={{ fontSize: 13 }}>
        <span className="pb-mono-tag" style={{ marginRight: 6 }}>{title}</span>
        <span className="pb-section-meta">
          · {count} {count === 1 ? 'source' : 'sources'}
        </span>
      </h3>
    </div>
    <div className="pb-list">
      {feeds.map((feed, index) => {
        const days = dormantDays(feed.dormant_since);
        const isThrottled =
          feed.next_poll_at && new Date(feed.next_poll_at) > new Date();
        const showEnable = feed.disabled === true || feed.status === 'error';
        const isEnabling = enabling?.[feed.source_name] ?? false;
        return (
          <div key={`${feed.source_name}-${index}`} className="pb-feed-row">
            <span className={`pb-feed-dot ${dotClass}`} aria-hidden="true" />
            <div className="pb-feed-name">
              <span className="pb-feed-name-label">{feed.source_name}</span>
              {feed.error_message ? (
                <span className="pb-feed-name-error">{feed.error_message}</span>
              ) : (
                <span
                  className="pb-feed-name-error"
                  style={{ color: 'var(--pb-ink-muted)' }}
                >
                  {feed.source_url}
                </span>
              )}
              {(days !== null || isThrottled) && (
                <span className="pb-feed-badges">
                  {days !== null && (
                    <span className="pb-feed-badge pb-feed-badge--dormant">
                      Dormant {days}d
                    </span>
                  )}
                  {isThrottled && (
                    <span className="pb-feed-badge pb-feed-badge--throttled">
                      Next poll: {formatDate(feed.next_poll_at)}
                    </span>
                  )}
                </span>
              )}
            </div>
            <span className="pb-feed-meta">
              <span className="pb-feed-meta-count">{feed.article_count}</span>
              <span>{feed.status}</span>
              <span aria-hidden="true">·</span>
              <span>{formatDate(feed.last_updated)}</span>
            </span>
            {showEnable && (
              <button
                type="button"
                className="pb-chip"
                onClick={() => onEnable(feed)}
                disabled={isEnabling}
              >
                Enable
              </button>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export default StatusPage;

export { StatusPage as StatusPanel };
